import { observer } from 'mobx-react';
import React from 'react';

import { UiButton,
    UiDatepicker,
    UiDescription,
    UiFormControl,
    UiIcon,
    UiInput,
    UiModal,
    UiModalActions,
    UiModalContent,
    UiModalTitle,
    UiReservationSelect,
    UiSelect,
    UiSwitch,
    UiUserSelect, } from 'shared/uikit-new';
import { COLORS } from 'shared/constants';
import { calendarItemsSaveQuery } from 'shared/queries';
import { CalendarItemEducationTypeEnum, CalendarItemTypeEnum } from 'shared/enums';
import { CalendarItemModel, ReservationModel } from 'shared/models';
import { useObservable, useValidationZod } from 'shared/hooks';

import { CALENDAR_PAGE } from 'pages/calendar/constants/CALENDAR_PAGE';
import { Store } from 'pages/calendar/store';

import './styles.scss';
import { toDate } from 'shared/utilities';

export const CalendarPageCalendarItemEdit = observer(function CalendarPageCalendarItemEdit() {
    const store = useObservable({
        isSubmitting: false,
        isSubmitted: false,
    });

    const {
        calendarItem,
        isOpened,
    } = Store.edit;

    const validation = useValidationZod(calendarItem, z => ({
        subject: z.string().min(1, 'обязательное поле'),
    }));

    const handleSubmit = async () => {
        store.set('isSubmitted', true);

        if (!validation.isValid || store.isSubmitting) {
            return;
        }

        store.set('isSubmitting', true);

        const response = await calendarItemsSaveQuery(calendarItem);
        if (response.isSuccess && response.data) {
            const id = response.data.item.id;
            const calendarItem = Store.calendarItems.find(item => item.id === id);
            if (calendarItem) {
                calendarItem.update(response.data.item);
            } else {
                Store.set('calendarItems', [...Store.calendarItems, new CalendarItemModel(response.data.item)]);
            }
        }
        Store.edit.set('isOpened', false);
        store.set('isSubmitting', false);
    };

    const startAtItems = (): { id: string | null; name: string }[] => {
        if (calendarItem.isAllDayEvent) {
            return [{
                id: null, name: '-',
            }];
        }
        const result: { id: string; name: string }[] = [];
        for (let i = CALENDAR_PAGE.HOURS[0]; i < CALENDAR_PAGE.HOURS[CALENDAR_PAGE.HOURS.length - 1]; i++) {
            result.push({
                id: `${i}`.padStart(2, '0') + `:00`,
                name: `${i}`.padStart(2, '0') + `:00`,
            });
            result.push({
                id: `${i}`.padStart(2, '0') + `:30`,
                name: `${i}`.padStart(2, '0') + `:30`,
            });
        }

        return result;
    };

    const endAtItems = (): { id: string | null; name: string }[] => {
        if (calendarItem.isAllDayEvent) {
            return [{
                id: null, name: '-',
            }];
        }

        const result: { id: string; name: string }[] = [];

        let date = calendarItem.startAtDate.add(30, 'minutes');
        const endOfDay = date
            .set('hours', CALENDAR_PAGE.HOURS[CALENDAR_PAGE.HOURS.length - 1])
            .set('minutes', 0)
            .add(1, 'minutes')
            .add(1, 'hour');
        while (date.isBefore(endOfDay)) {
            result.push({
                id: date.format('HH:mm'),
                name: date.format('HH:mm'),
            });
            date = date.add(30, 'minutes');
        }

        return result;
    };

    const reminderMinutesBeforeStartItems = () => {
        return [{
            id: 0, name: 'Никогда',
        }, {
            id: 1, name: 'За 1 минуту',
        }, {
            id: 5, name: 'За 5 минут',
        }, {
            id: 15, name: 'За 15 минут',
        }, {
            id: 30, name: 'За 30 минут',
        }, {
            id: 60, name: 'За 1 час',
        }, {
            id: 120, name: 'За 2 часа',
        }, {
            id: 180, name: 'За 3 часа',
        }];
    };

    const title = () => {
        const name = calendarItem.type.name.toLowerCase();
        if (calendarItem.id) {
            return `Изменить ${name}`;
        }
        return `Добавить ${name}`;
    };

    const updateSchedule = (startAt: string | null, endAt: string | null) => {
        calendarItem.update({
            startAt,
            endAt,
            reservation: calendarItem.reservation
                ? new ReservationModel({
                    ...calendarItem.reservation,
                    startAt: startAt || '',
                    endAt: endAt || '',
                })
                : calendarItem.reservation,
        });
    };

    return (
        <UiModal isOpened={isOpened} onClose={() => Store.edit.set('isOpened', false)} isAside>
            <UiModalTitle className={'calendar-page-calendar-item-edit__title'}>
                {!!calendarItem.id && <UiIcon.PencilLine size={28} />}
                {!calendarItem.id && <UiIcon.SquarePlus size={28} />}
                <span>{title()}</span>
            </UiModalTitle>
            <UiModalContent>
                <UiFormControl className="calendar-page-calendar-item-edit__subject" error={store.isSubmitted && validation.subject.error}>
                    <UiInput template={'large'} placeholder={'Введите название'} name={'subject'} value={calendarItem.subject} onChange={calendarItem.handleChange} />
                </UiFormControl>
                <UiDescription className={'calendar-page-calendar-item-edit__body'} name={'body'} value={calendarItem.body} onChange={calendarItem.handleChange} />
                <div className={'calendar-page-calendar-item-edit-dates'}>
                    <UiFormControl label={'Дата'}>
                        <UiDatepicker
                            scrollIntoView
                            name={'startAt'}
                            value={calendarItem.startAt}
                            onChange={data => {
                                if (!calendarItem.reservationId) {
                                    calendarItem.handleChange(data);
                                    calendarItem.update({
                                        reservationId: null,
                                        reservation: null,
                                    });
                                    return;
                                }

                                const startAt = toDate(data.value)
                                    .set('hour', calendarItem.startAtDate.hour())
                                    .set('minute', calendarItem.startAtDate.minute());
                                const duration = Math.max(calendarItem.endAtDate.diff(calendarItem.startAtDate, 'minute'), 30);
                                updateSchedule(startAt.format(), startAt.add(duration, 'minute').format());
                            }}
                        />
                    </UiFormControl>
                    <div className="calendar-page-calendar-item-edit-dates__sub">
                        <UiFormControl label={'Начало'}>
                            <UiSelect
                                scrollIntoView
                                isDisabled={calendarItem.isAllDayEvent}
                                items={startAtItems()}
                                onChange={({ value }) => {
                                    if (!value) {
                                        return;
                                    }
                                    const hours = value.split(':')[0];
                                    const minutes = value.split(':')[1];
                                    const nextStartAt = calendarItem.startAtDate.set('hours', +hours)
                                        .set('minutes', +minutes);
                                    const nextEndAt = nextStartAt.isAfter(calendarItem.endAtDate)
                                        ? nextStartAt.add(30, 'minutes')
                                        : calendarItem.endAtDate;

                                    updateSchedule(
                                        nextStartAt.format(),
                                        nextEndAt.format(),
                                    );
                                }}
                                value={calendarItem.isAllDayEvent ? null : calendarItem.startAtDate.format('HH:mm')}
                                slots={{
                                    controlValue: ({ value }) => (
                                        <>
                                            <UiIcon.Clock8 size={24} />
                                            <span>{value}</span>
                                        </>
                                    ),
                                }}
                            />
                            <UiSwitch
                                template={'large'}
                                className={'calendar-page-calendar-item-edit__all-day-event'}
                                name={'isAllDayEvent'}
                                value={calendarItem.isAllDayEvent}
                                onChange={calendarItem.handleChange}
                            >
                                Весь день
                            </UiSwitch>
                        </UiFormControl>
                        <UiFormControl label={'Окончание'}>
                            <UiSelect
                                scrollIntoView
                                isDisabled={calendarItem.isAllDayEvent}
                                items={endAtItems()}
                                onChange={({ value }) => {
                                    if (!value) {
                                        return;
                                    }
                                    updateSchedule(
                                        calendarItem.startAt,
                                        calendarItem.endAtDate
                                            .set('hours', +value.split(':')[0])
                                            .set('minutes', +value.split(':')[1])
                                            .format(),
                                    );
                                }}
                                value={calendarItem.isAllDayEvent ? null : calendarItem.endAtDate.format('HH:mm')}
                                slots={{
                                    controlValue: ({ value }) => (
                                        <>
                                            <UiIcon.Clock8 size={24} />
                                            <span>{value}</span>
                                        </>
                                    ),
                                }}
                            />
                        </UiFormControl>
                    </div>
                </div>
                {calendarItem.type.is(CalendarItemTypeEnum.Education) && (
                    <UiFormControl label={'Тип обучения'} className={'calendar-page-calendar-item-edit__education-type-id'}>
                        <UiSelect
                            scrollIntoView
                            placeholder={'Выберите тип обучения'}
                            items={CalendarItemEducationTypeEnum.items}
                            onChange={calendarItem.handleChange}
                            name={'educationTypeId'}
                            value={calendarItem.educationTypeId}
                            slots={{
                                controlValue: ({ value }) => (
                                    <>
                                        <UiIcon.GraduationCap size={24} />
                                        <span>{value}</span>
                                    </>
                                ),
                            }}
                        />
                    </UiFormControl>
                )}
                {!calendarItem.type.is(CalendarItemTypeEnum.Reminder) && (
                    <UiFormControl className={'calendar-page-calendar-item-edit__attendees'} label={'Участники'}>
                        <UiUserSelect
                            multiple
                            scrollIntoView
                            onChange={calendarItem.handleChange}
                            name={'attendees'}
                            value={calendarItem.attendees}
                            preventReSelection={true}
                        />
                    </UiFormControl>
                )}
                <UiFormControl className={'calendar-page-calendar-item-edit__attendees'} label={'Переговорная комната'}>
                    <UiReservationSelect
                        date={calendarItem.startAt || toDate().format()}
                        onChange={({ value }) => {
                            calendarItem.update({
                                reservationId: value ? value.id : null,
                                reservation: value,
                            });
                        }}
                        value={calendarItem.reservation}
                    />
                </UiFormControl>
                <UiFormControl className={'calendar-page-calendar-item-edit__reminder'} label={'Напоминание'}>
                    <UiSelect
                        scrollIntoView
                        items={reminderMinutesBeforeStartItems()}
                        onChange={calendarItem.handleChange}
                        name={'reminderMinutesBeforeStart'}
                        value={calendarItem.reminderMinutesBeforeStart}
                        slots={{
                            controlValue: ({ value }) => (
                                <>
                                    <UiIcon.AlarmClockCheck size={24} />
                                    <span>{value}</span>
                                </>
                            ),
                        }}
                    />
                </UiFormControl>
            </UiModalContent>
            <UiModalActions>
                <UiButton onClick={handleSubmit} template={'medium'} isLoading={store.isSubmitting}>
                    {calendarItem.id ? 'Сохранить' : 'Создать'}
                </UiButton>
                <UiButton
                    onClick={() => Store.edit.set('isOpened', false)}
                    template={'medium'}
                    style={{
                        backgroundColor: [COLORS._F3F5F8, COLORS._C6CAD1],
                        color: [COLORS._2C2F35, COLORS._2C2F35],
                    }}
                >
                    Отмена
                </UiButton>
            </UiModalActions>
        </UiModal>
    );
});
